import { RoomEvent, rtcDetector, utils } from '..';
import { ENV_NAME, BACKEND_ENV, Scene, version } from '../common/constants';
import { getMicrophones, getCameras } from '../common/device-detector';
import ErrorCode from '../common/error-code';
import { generateErrorMessage } from '../common/error-generator';
import { ErrorName } from '../common/error-info';
import RtcError from '../common/rtc-error';
import { UserRoleNumber, UserRole, IRoom } from '../enums';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT from '../inner-emitter/inner-event';
import { BaseRoom } from '../room-base';
import { ProcessHandler, createDecorator } from '../utils/decorators/middleware';
import eventLogger from '../utils/log/event-log';
import { IS_LOCAL } from '../utils/environment';
import { detectVideoCodecCapabilities } from '../common/rtc-detection';
/**
 * // 记录正在进房或已经进房的 userId
 * // 用于拦截业务侧重复创建相同 userId client 进相同房间的行为。
 * @class JoinHelper
 */
class JoinHelper {
  // key: ${sdkAppId}_${roomId}_${userId}_${useStringRoomId}
  private _set = new Set();
  constructor() {
    innerEmitter.on(INNER_EVENT.LEAVE_START, this.delete, this);
    innerEmitter.on(INNER_EVENT.SWITCH_ROOM_SUCCESS, this.handleSwitchRoomSuccess, this);
  }

  add({ room, roomId }: { room: IRoom, roomId?: string | number; }) {
    if (room.scene === Scene.RTC) {
      return;
    }
    const key = this.getKey(
      room.userId,
      // JOIN_START 事件执行时，roomId 是默认值 0，此时需使用 client.join({ roomId }) 传的 roomId
      roomId || room.roomId,
      room.sdkAppId,
      room.useStringRoomId
    );
    this._set.add(key);
  }

  delete({ room, roomId }: { room: IRoom, roomId: string | number; }) {
    if (room.scene === Scene.RTC) {
      return;
    }
    const key = this.getKey(
      room.userId,
      room.roomId || roomId,
      room.sdkAppId,
      room.useStringRoomId
    );
    this._set.delete(key);
  }

  getKey(userId: string, roomId: string | number, sdkAppId: number, useStringRoomId: boolean) {
    return `${sdkAppId}_${roomId}_${userId}_${useStringRoomId}`;
  }

  isJoined({ userId, roomId, sdkAppId, room, role }: { userId: string, roomId: string | number, sdkAppId: number, room: IRoom, role?: number; }) {
    if (room.scene === Scene.RTC) {
      return false;
    }
    if (role !== UserRoleNumber.AUDIENCE) {
      return false;
    }
    return this._set.has(this.getKey(userId, roomId, sdkAppId, room.useStringRoomId));
  }

  handleSwitchRoomSuccess({ room, currentRoomId, targetRoomId }: { room: IRoom, currentRoomId: string | number, targetRoomId: string | number; }) {
    if (room.scene === Scene.RTC) {
      return;
    }
    this._set.delete(this.getKey(room.userId, currentRoomId, room.sdkAppId, room.useStringRoomId));
    this._set.add(this.getKey(room.userId, targetRoomId, room.sdkAppId, room.useStringRoomId));
  }
}
async function uploadTrtcStats(this: BaseRoom) {
  let micNum;
  let videoNum;
  /**
 * get the num count of microphone devices.
 */
  try {
    const audioInputData = await getMicrophones();
    micNum = audioInputData && audioInputData.length;
  } catch (error) {
    // ignore
  }
  /**
 * get the num count of camera devices.
 */
  try {
    const videoInputData = await getCameras();
    videoNum = videoInputData && videoInputData.length;
  } catch (error) {
    // ignore
  }
  /**
 * set the params of trtcStats.
 */
  const devices = {
    microphone: micNum,
    camera: videoNum
  };

  const {
    isH264EncodeSupported,
    isVp8EncodeSupported,
    isH264DecodeSupported,
    isVp8DecodeSupported,
    isH265EncodeSupported,
    isH265DecodeSupported
  } = this.checkSystemResult.detail;

  const basis = rtcDetector.basis();
  const trtc = {
    webRTC: basis.isWebRTCSupported,
    getUserMedia: basis.isGetUserMediaSupported,
    webSocket: basis.isWebSocketsSupported,
    screenShare: basis.isScreenShareSupported,
    webAudio: basis.isWebAudioSupported,
    h264Encode: isH264EncodeSupported,
    h264Decode: isH264DecodeSupported,
    vp8Encode: isVp8EncodeSupported,
    vp8Decode: isVp8DecodeSupported,
    h265Encode: isH265EncodeSupported,
    h265Decode: isH265DecodeSupported
  };
  const trtcStats = {
    browser: basis.browser,
    os: basis.os,
    trtc,
    devices
  };

  const trtcAdvanced = {
    isWebCodecSupported: basis.isWebCodecSupported,
    isMediaSessionSupported: basis.isMediaSessionSupported,
    isWebTransportSupported: basis.isWebTransportSupported
  };

  // 检索方式：log:trtcstats*
  eventLogger.uploadEvent({
    log: `trtcstats-${JSON.stringify(trtcStats)}`,
    userId: this.userId
  });
  this._log.info(`TrtcStats-${JSON.stringify(trtcStats)}`);
  eventLogger.uploadEvent({
    log: `trtcadvancedstats-${JSON.stringify(trtcAdvanced)}`,
    userId: this.userId
  });
  detectVideoCodecCapabilities();
}
export function beforeJoin() {
  return createDecorator((join: ProcessHandler<BaseRoom>) => {
    const joinHelper = new JoinHelper();
    return async function (this: BaseRoom, params: TRTCParams, scene: Scene, frameWorkType: number) {
      const roomId = String(params.roomId || params.strRoomId);
      this.userId = params.userId;
      this.sdkAppId = params.sdkAppId;
      this.userSig = params.userSig;
      this._log.setSdkAppId(this.sdkAppId);
      this._log.setUserId(this.userId);
      this.scene = scene;
      params.privateMapKey = params.privateMapKey || '';
      // const startTime = utils.performanceNow();
      if (this.isJoined) {
        throw new RtcError({
          code: ErrorCode.INVALID_OPERATION,
          message: generateErrorMessage({ key: ErrorName.INVALID_JOIN })
        });
      }
      this.checkDestroy();
      if (
        joinHelper.isJoined({
          userId: this.userId,
          roomId,
          sdkAppId: this.sdkAppId,
          room: this,
          role: params.role
        })
      ) {
        throw new RtcError({
          code: ErrorCode.INVALID_OPERATION,
          message: generateErrorMessage({
            key: ErrorName.REPEAT_JOIN,
            data: this.userId
          })
        });
      }
      joinHelper.add({ room: this, roomId });
      this.role = params.role === UserRoleNumber.AUDIENCE ? UserRole.AUDIENCE : UserRole.ANCHOR;
      this._log.info(`Join() => joining room: ${roomId} useStringRoomId: ${this.useStringRoomId} scene: ${this.scene} role: ${this.role}`);
      innerEmitter.emit(INNER_EVENT.JOIN_START, { room: this, roomId, params });

      // configure logEvent
      let env = utils.getEnv();
      if (!env) {
        env = ENV_NAME.QCLOUD;
        if (this.proxy_ws) {
          if (this.proxy_ws.startsWith(BACKEND_ENV.OLD_CLOUD_LADDER)) {
            // 旧云梯环境，目前只给呼叫中心使用
            env = ENV_NAME.OLD_CLOUD_LADDER;
          } else if (this.proxy_ws.startsWith(BACKEND_ENV.WEBRTC)) {
            // 自研环境，电话 SDK 和腾讯课堂使用
            env = ENV_NAME.WEBRTC;
          }
        }
      }
      eventLogger.setConfig({ env, sdkAppId: String(this.sdkAppId), userId: this.userId, roomId });

      rtcDetector.checkSystemRequirementsInternal(frameWorkType).then((result: any) => {
        this.checkSystemResult = result;
        uploadTrtcStats.call(this);
      });
      try {
        // 设置了 proxy server 则不请求调度。
        // 已请求过信令调度，也不再请求
        // 已预连接，也不再请求
        // whip 拉流，也不再请求
        if (!this.whipQuickStartConfig && !this.preconnectPromise && !this.proxy_ws && !this.proxy_wt && !this.scheduleResult.domains && !utils.getEnv()) {
          await this.schedule(params, frameWorkType);
        }
        const res = await join.call(this, params, scene, frameWorkType);
        this.roomId = roomId;
        this._joinedTimestamp = utils.performanceNow();
        innerEmitter.emit(INNER_EVENT.JOIN_SUCCESS, { room: this });
        // @ts-ignore
        // 只上报 sdk 的数据，call room 上层组件不上报
        if (frameWorkType === 30 && !params.component) {
          eventLogger.uploadEvent({
            log: `stat-conv-${Number(IS_LOCAL)}-${location.hostname}`,
            userId: this.userId
          });
        }
        return res;
      } catch (error) {
        joinHelper.delete({ room: this, roomId });
        innerEmitter.emit(INNER_EVENT.JOIN_FAILED, { room: this, error });
        throw error;
      }
    };
  });
}
